import { useEffect, useState } from 'react';
import { centerPerfAPI } from '../../api/client';
import { COLORS } from '../../utils/uiStyles';

/* 센터 실적 «기준 데이터» — [월 목표]·[고객사 마스터] 소구역(BPO 가져오기로 들어온 값을 보기만 한다).
 * · 범위는 서버가 정한다(/center-perf/bpo/targets·customers — 조회 범위 안 실적 센터만, 미배정 원문은 전부 범위만).
 * · 연도를 처음엔 빼고 부른다 — 서버가 저장된 가장 최근 연도를 골라 years 와 함께 돌려준다.
 */
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

const card = { background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: '10px', padding: '14px', marginBottom: '12px' };
const th = { padding: '7px 9px', textAlign: 'left', fontSize: '12px', color: COLORS.textSub, whiteSpace: 'nowrap', borderBottom: `2px solid ${COLORS.border}` };
const td = { padding: '5px 9px', fontSize: '13px', borderBottom: `1px solid ${COLORS.borderSoft}`, whiteSpace: 'nowrap' };
const tdNum = { ...td, textAlign: 'right' };
const inp = { padding: '5px 8px', border: `1px solid ${COLORS.inputBorder}`, borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' };
const btn = (bg, color = '#fff') => ({ padding: '5px 10px', background: bg, color, border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 });
const hint = { fontSize: '12px', color: COLORS.textMuted, marginBottom: '8px' };

const detailOf = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' && d ? d : fallback;
};
export const fmtNum = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString('ko-KR') : String(v);
};

// 응답 연도 + 저장된 연도 목록(중복 없이 큰 연도부터) — 선택한 연도가 비어 있어도 고를 수 있게 남긴다.
const yearOptions = (data) => {
  const set = new Set([...(data?.years || []), ...(data?.year ? [data.year] : [])]);
  return Array.from(set).sort((a, b) => b - a);
};

function YearSelect({ label, data, onChange }) {
  return (
    <select aria-label={label} value={data?.year ?? ''} style={inp} onChange={(e) => onChange(Number(e.target.value))}>
      {yearOptions(data).map((y) => <option key={y} value={y}>{y}년</option>)}
    </select>
  );
}

function TargetsTable() {
  const [year, setYear] = useState(undefined);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setError('');
    centerPerfAPI.refTargets(year)
      .then((r) => { if (alive) setData(r.data || null); })
      .catch((e) => { if (alive) { setData(null); setError(detailOf(e, '월 목표를 불러오지 못했습니다.')); } });
    return () => { alive = false; };
  }, [year]);

  const centers = data?.centers || [];
  const unassigned = data?.unassigned || [];
  return (
    <div style={card}>
      <div style={hint}>BPO 파일 «센터계획(연도)» 시트 오른쪽 세로 블록(센터·월·계획)에서 가져온 월 목표입니다. 실적 센터 순서대로 보입니다.</div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
        <YearSelect label="목표 연도" data={data} onChange={setYear} />
      </div>
      {error && <div role="alert" style={{ color: COLORS.dangerText, fontSize: '13px', marginBottom: '8px' }}>{error}</div>}
      <div style={{ overflowX: 'auto' }}>
        <table data-testid="perf-targets" style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={th}>실적 센터</th>
              {MONTHS.map((m) => <th key={m} style={{ ...th, textAlign: 'right' }}>{m}월</th>)}
              <th style={{ ...th, textAlign: 'right' }}>합계</th>
            </tr>
          </thead>
          <tbody>
            {data && centers.length === 0 && (
              <tr><td colSpan={14} style={{ ...td, color: COLORS.textMuted }}>이 연도의 월 목표가 없습니다.</td></tr>
            )}
            {centers.map((c) => (
              <tr key={c.perf_center} data-center={c.perf_center}>
                <td style={{ ...td, fontWeight: 600 }}>{c.perf_center}</td>
                {MONTHS.map((m) => <td key={m} data-col={`m${m}`} style={tdNum}>{fmtNum(c.months?.[m - 1])}</td>)}
                <td data-col="total" style={{ ...tdNum, fontWeight: 600 }}>{fmtNum(c.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {unassigned.length > 0 && (
        <div data-testid="perf-targets-unassigned" style={{ marginTop: '8px', fontSize: '12px', color: COLORS.warn }}>
          미배정 센터명(실적 센터 별칭에 없음): {unassigned.map((u) => `${u.center_raw || '(빈칸)'}(합계 ${fmtNum(u.total)})`).join(', ')}
        </div>
      )}
    </div>
  );
}

function CustomersTable() {
  const [year, setYear] = useState(undefined);
  const [query, setQuery] = useState('');           // 입력 중인 검색어
  const [q, setQ] = useState('');                   // 보낸 검색어
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setError('');
    centerPerfAPI.refCustomers(year, q)
      .then((r) => { if (alive) setData(r.data || null); })
      .catch((e) => { if (alive) { setData(null); setError(detailOf(e, '고객사 마스터를 불러오지 못했습니다.')); } });
    return () => { alive = false; };
  }, [year, q]);

  const rows = data?.customers || [];
  const search = (e) => {
    e.preventDefault();
    setQ(query.trim());
  };
  return (
    <div style={card}>
      <div style={hint}>BPO 파일 «고객사리스트(연도)» 시트에서 가져온 고객사 마스터입니다.</div>
      <form onSubmit={search} style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '8px' }}>
        <YearSelect label="고객사 연도" data={data} onChange={setYear} />
        <input aria-label="고객사 검색" value={query} maxLength={100} placeholder="센터·고객사·담당·팀 검색" style={{ ...inp, width: '220px' }}
          onChange={(e) => setQuery(e.target.value)} />
        <button type="submit" style={btn(COLORS.primary)}>검색</button>
      </form>
      {error && <div role="alert" style={{ color: COLORS.dangerText, fontSize: '13px', marginBottom: '8px' }}>{error}</div>}
      {data && (
        <div data-testid="perf-customers-count" style={{ fontSize: '12px', color: COLORS.textSub, marginBottom: '6px' }}>
          {data.total}건{data.truncated ? ` (앞 ${rows.length}건만 표시)` : ''}
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table data-testid="perf-customers" style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>{['실적 센터', '센터', '센터&고객사', '사업자등록증 명칭', '기/신/종', '담당', '팀명'].map((h) => <th key={h} style={th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {data && rows.length === 0 && (
              <tr><td colSpan={7} style={{ ...td, color: COLORS.textMuted }}>고객사가 없습니다.</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={`${r.source_row}-${i}`} data-customer={r.center_customer || ''}>
                <td data-col="perf_center" style={td}>
                  {r.perf_center || <span style={{ color: COLORS.warn }}>미배정</span>}
                </td>
                <td data-col="center_raw" style={td}>{r.center_raw || '—'}</td>
                <td data-col="center_customer" style={{ ...td, whiteSpace: 'normal' }}>{r.center_customer || '—'}</td>
                <td data-col="biz_name" style={{ ...td, whiteSpace: 'normal' }}>{r.biz_name || '—'}</td>
                <td data-col="kind" style={td}>{r.kind || '—'}</td>
                <td data-col="manager" style={td}>{r.manager || '—'}</td>
                <td data-col="team" style={td}>{r.team || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function RefTablesSection({ kind }) {
  return kind === 'customers' ? <CustomersTable /> : <TargetsTable />;
}
